import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["logo", "color", "id", "classes", "className", "component", "edsContext"];
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { default as withStyles } from '../styles';
import { withEDSContext } from '../EDSContext/EDSContext';
import uuid from 'uuid/v4';
import { colorFillLogoPreferred, colorFillLogoAlternativePowerButton, colorFillLogoAlternativeText, colorBrandPrimary, spacing40, sizingXxLarge1, heightFluid, widthFluid } from '../styles/tokens';

// TODO: Account for custom logos

export var styles = function styles() {
  return {
    root: {
      height: sizingXxLarge1,
      minWidth: '7.5rem',
      padding: spacing40
    },
    logoContainer: {
      height: heightFluid,
      width: widthFluid,
      display: 'inline-block',
      maxHeight: 'none',
      fill: colorFillLogoPreferred // by default assign to preferred (white) fill color
    },
    // alternative = black color
    logoAlternativeLogo: {
      fill: colorFillLogoAlternativePowerButton // to be removed
    },
    logoAlternativeText: {
      fill: colorFillLogoAlternativeText
    },
    // iris is primary brand color, not themed
    logoIrisLogo: {
      fill: colorBrandPrimary
    }
  };
};

/**
 * Use `Logo` to display the logo within the top header bar.
 * @done true
 * @updated v7.18.0
 * @versionAdded v0.0.5
 * @examples
 *  SimpleExample
 *  Colors
 *  ClickExample
 *  NavLinkExample
 *  RedirectExample
 */
var Logo = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _classNames;
  var logo = props.logo,
    color = props.color,
    id = props.id,
    classes = props.classes,
    classNameProp = props.className,
    ComponentProp = props.component,
    edsContext = props.edsContext,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  var logoTextClasses = classNames((_classNames = {}, _classNames[classes.logoAlternativeText] = logo === 'alternative' || color === 'black', _classNames[classes.logoIrisLogo] = logo === 'iris' || color === 'iris', _classNames));

  // Only id the logo if original id present.
  var svgId = id ? id + "_LogoSvg" : "" + uuid();
  return /*#__PURE__*/React.createElement(ComponentProp, _extends({
    id: id,
    className: classNames(classes.root, classNameProp)
  }, rest, {
    ref: ref
  }), /*#__PURE__*/React.createElement("svg", {
    id: svgId,
    className: classNames(classes.logoContainer, logoTextClasses),
    role: "img",
    "aria-labelledby": "logo_description_" + svgId,
    width: "132",
    height: "32",
    viewBox: "0 0 132 32",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("desc", {
    id: "logo_description_" + svgId
  }, edsContext.formatMessage('component.Logo.logo')), /*#__PURE__*/React.createElement("path", {
    fill: "inherit",
    d: "M20.7362 0.705234H25.6024V31.5592H20.7362V0.705234ZM28.2325 31.5592H33.0988V0.705234H28.2325V31.5592ZM51.029 24.1102C49.933 25.9614 47.8287 27.1076 45.6806 27.1076C42.6117 27.1076 40.5951 24.9919 40.5951 21.5096V10.0055H35.7289V22.303C35.7289 28.2534 39.1046 32 44.4969 32C47.1712 32 49.7578 30.7218 51.029 28.6501V31.5592H55.9392V10.0055H51.029V24.1102ZM68.5211 27.2837C64.9701 27.2837 62.2519 24.4628 62.2519 20.8043C62.2519 17.1018 64.9701 14.2809 68.5211 14.2809C71.0637 14.2809 73.2558 15.7354 73.782 17.7189H78.7798C78.1222 13.0467 73.6943 9.56462 68.5211 9.56462C62.2519 9.56462 57.4295 14.4131 57.4295 20.8043C57.4295 27.1955 62.2519 31.9999 68.5211 31.9999C73.7381 31.9999 78.2536 28.5619 78.8235 23.6693H73.8257C73.2997 25.8291 71.0637 27.2837 68.5211 27.2837ZM80.3582 31.5592H85.2244V10.0055H80.3582V31.5592ZM82.8132 0C81.1036 0 79.7007 1.45455 79.7007 3.12948C79.7007 4.89256 81.1036 6.30303 82.8132 6.30303C84.5229 6.30303 85.9697 4.89256 85.9697 3.12948C85.9697 1.45455 84.523 0 82.8132 0ZM103.9 10.0055H108.81V31.5592H103.9V28.562C102.848 30.5455 99.9545 32 97.061 32C91.2302 32 86.7585 27.0634 86.7585 20.7603C86.7585 14.5014 91.2303 9.56474 97.061 9.56474C99.9545 9.56474 102.848 11.0193 103.9 13.0028V10.0055ZM103.9 17.5868C102.892 15.6474 100.524 14.2369 98.0693 14.2369C94.3867 14.2369 91.5371 17.0138 91.5371 20.7603C91.5371 24.5069 94.3869 27.3278 98.0693 27.3278C100.524 27.3278 102.848 25.9174 103.9 24.0219V17.5868ZM122.753 9.56474C120.079 9.56474 117.58 10.843 116.265 12.8705V10.0055H111.355V31.5592H116.265V17.4105C117.405 15.6033 119.421 14.4573 121.569 14.4573C124.638 14.4573 126.655 16.573 126.655 20.0551V31.5592H131.521V19.2617C131.521 13.3113 128.146 9.56474 122.753 9.56474ZM0 5.11295H18.1061V0.705234H0V5.11295ZM4.38404 13.9284H13.7221V18.3361H4.38404V22.7438H18.1061V9.52066H4.38404V13.9284ZM0 31.5592H18.1061V27.1515H0V31.5592Z"
  })));
});
Logo.propTypes = {
  /**
   * Class name prop for logo container.
   */
  className: PropTypes.string,
  /**
   * Prop used to add additional styling to the component.
   */
  classes: PropTypes.object,
  /**
   * The component used for the root node.
   * Either a string to use a DOM element or a component.
   */
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  /**
   * HTML ID attribute for uniqueness.
   */
  id: PropTypes.string,
  /**
   * DEPRECATED: use `color` prop instead.
   *
   * Set the color style of the logo.
   *
   * - `preferred` will render the logo in white.
   * - `alternative` will render the logo in black.
   * - `iris` will render the logo in the Ellucian's primary brand color.
   */
  logo: PropTypes.oneOf(['preferred', 'alternative', 'iris']),
  /**
   * Set the color of the logo.
   */
  color: PropTypes.oneOf(['white', 'black', 'iris']),
  /**
   * @ignore
   */
  edsContext: PropTypes.object
};
Logo.displayName = 'Logo';
Logo.defaultProps = {
  logo: 'preferred',
  color: 'white',
  component: 'div'
};
export default withEDSContext(withStyles(styles)(Logo));